import React from 'react'
import styled, { css } from 'styled-components'

type Props = {
  sticky?: boolean
  /** RTL sticky offset in px from the inline-end (right) edge. */
  offset?: number
  /** Edge shadow on the last frozen column, against the scrolling area. */
  lastFrozen?: boolean
  /** Leading accent columns. */
  highlight?: boolean
  width?: string
  children?: React.ReactNode
}

const SellerHeadCell = ({
  sticky,
  offset = 0,
  lastFrozen,
  highlight,
  width,
  children,
}: Props) => (
  <Th
    $sticky={!!sticky}
    $offset={offset}
    $lastFrozen={!!lastFrozen}
    $highlight={!!highlight}
    $width={width}
  >
    {children}
  </Th>
)

export default SellerHeadCell

const Th = styled.th<{
  $sticky: boolean
  $offset: number
  $lastFrozen: boolean
  $highlight: boolean
  $width?: string
}>`
  position: sticky;
  top: 0;
  /* Frozen column headers (corner cells) must layer above the plain sticky header row. */
  z-index: ${({ $sticky }) => ($sticky ? 3 : 2)};
  background: ${({ theme, $highlight }) =>
    $highlight ? theme.colors.accentSoft : theme.colors.bgLayout};
  text-align: center;
  padding: 11px 14px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
  white-space: nowrap;
  user-select: none;
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
      min-width: ${$width};
    `}
  ${({ $sticky }) =>
    $sticky &&
    css`
      /* Frozen header cells keep vertical sticky (top: 0) but are pinned
         horizontally by the same manual freeze as the body cells: translateX
         by --freeze-x (set on the scroll container) on horizontal scroll. */
      transform: translateX(var(--freeze-x, 0px));
      will-change: transform;
    `}
  ${({ $lastFrozen }) =>
    $lastFrozen &&
    css`
      box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.12);
    `}
`
